import { Checkbox, Radio, TableColumnsType, TableProps } from "antd";
import {
  ExpandTable,
  ExpandTableProps,
} from "components/atoms/table/AntdTable";
import { useIsMobile } from "components/MediaQuery";
import { useCallback, useMemo, useRef } from "react";
import InfiniteWrapper from "./InfiniteWrapper";

interface InfiniteExpandTableProps extends ExpandTableProps {
  data: any[];
  loadMore: () => void;
  isLoading: boolean;
  hasMore: boolean;
  leftPadding?: number;
  isDoubleTree?: boolean;
  initialScrollTop?: number;
  onScrollPosition?: (scrollTop: number) => void;
}

export default function InfiniteExpandTable(props: InfiniteExpandTableProps) {
  const isMobile = useIsMobile();

  const {
    data,
    loadMore,
    isLoading,
    hasMore,
    columns,
    leftPadding,
    isDoubleTree,
    initialScrollTop = 0,
    onScrollPosition,
    rowSelection,
    rowKey,
    ...rest
  } = props;

  const rowSelectionRef = useRef(rowSelection);
  rowSelectionRef.current = rowSelection;
  const hasRowSelection = Boolean(rowSelection);

  const getRecordKey = useCallback(
    (record: Record<string, unknown>): React.Key => {
      if (typeof rowKey === "function") {
        return rowKey(record);
      }
      if (typeof rowKey === "string") {
        return record[rowKey] as React.Key;
      }
      return record.key as React.Key;
    },
    [rowKey]
  );

  // rowSelection ref avoids rebuilding columns on every selectedRowKeys change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const expandColumns = useMemo(() => {
    const expandLeftPadding = leftPadding ?? 0;

    const width = () => {
      let result = expandLeftPadding;

      if (!isMobile) {
        result += 48;
      }

      if (isDoubleTree) {
        result += (isMobile ? 10 : 48) + expandLeftPadding;
      }

      return result;
    };

    const baseColumns: TableColumnsType<any> = [
      {
        key: "expandColumn",
        render: () => <div></div>,
        width: width(),
      },
    ];

    if (!rowSelection) {
      return [...baseColumns, ...(columns || [])];
    }

    const isRadio = rowSelection.type === "radio";
    const selectionWidth = rowSelection.columnWidth ?? (isMobile ? 40 : 48);

    const selectionColumn: TableColumnsType<any>[number] = {
      key: "expand-selection",
      width: selectionWidth,
      className: "ant-table-selection-column",
      align: "center",
      render: (_: unknown, record: any, index: number) => {
        const selection = rowSelectionRef.current;
        if (!selection) return null;

        const { onChange, getCheckboxProps, renderCell } = selection;
        const checkboxProps = getCheckboxProps?.(record) ?? {};
        const disabled = checkboxProps.disabled ?? false;
        const recordKey = getRecordKey(record);
        const selectionKey = (record.accountId ?? recordKey) as React.Key;
        const selectedKeys = selection.selectedRowKeys ?? [];
        const checked =
          selectedKeys.includes(selectionKey) ||
          selectedKeys.includes(recordKey) ||
          (record.key != null && selectedKeys.includes(record.key));

        const handleChange = () => {
          if (disabled) return;
          if (isRadio) {
            onChange?.([selectionKey], [record], { type: "single" });
            return;
          }
          const next = selectedKeys.includes(selectionKey)
            ? selectedKeys.filter((k) => k !== selectionKey)
            : [...selectedKeys, selectionKey];
          const nextRows = data.filter((row) =>
            next.some((k) => k === getRecordKey(row) || k === row.accountId)
          );
          onChange?.(next, nextRows, { type: "multiple" });
        };

        const control = isRadio ? (
          <Radio
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
          />
        );

        return renderCell
          ? renderCell(checked, record, index, control)
          : control;
      },
    };

    return [...baseColumns, selectionColumn, ...(columns || [])];
  }, [
    columns,
    data,
    getRecordKey,
    isDoubleTree,
    isMobile,
    leftPadding,
    hasRowSelection,
    rowSelection?.type,
    rowSelection?.columnWidth,
  ]);

  return (
    <InfiniteWrapper
      data={data}
      loadMore={loadMore}
      isLoading={isLoading}
      hasMore={hasMore}
      initialScrollTop={initialScrollTop}
      onScrollPosition={onScrollPosition}
    >
      <ExpandTable
        {...(rest as TableProps<any>)}
        rowKey={rowKey}
        columns={expandColumns}
        dataSource={data}
        showHeader={false}
        pagination={false}
        loading={false}
        locale={{
          emptyText: data.length === 0 && !isLoading ? "No data" : "",
        }}
      />
    </InfiniteWrapper>
  );
}
